import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useApi } from '../providers/api-provider';
import { useAuth } from '../providers/auth-provider';
import { CourseFeedbackPromptDto, PublicReviewDto, PublicReviewsDto } from '../general/dtos';
import { useBookmarkSelection } from '../general/bookmark-selection';
import { CourseFeedbackDialog } from '../components/dialogs/course-feedback-dialog';
import '../assets/theme-content.css';
import '../assets/reviews.css';

const reviewSortOptions = [
    { value: 'newest', label: 'Review date: newest first' },
    { value: 'oldest', label: 'Review date: oldest first' },
    { value: 'highest', label: 'Stars: highest first' },
    { value: 'lowest', label: 'Stars: lowest first' },
];

function Stars({ value }: { value: number }) {
    const rounded = Math.round(value * 2) / 2;
    return <span className="reviews-stars" role="img" aria-label={`${value} out of 5 stars`}>
        {Array.from({ length: 5 }, (_, index) => <i key={index} aria-hidden="true" className={`pi ${index + 1 <= rounded ? 'pi-star-fill' : index < rounded ? 'pi-star-half-fill' : 'pi-star'}`} />)}
    </span>;
}

function ReviewCard({ review, expanded, onToggle }: { review: PublicReviewDto; expanded: boolean; onToggle: () => void }) {
    const textRef = useRef<HTMLQuoteElement>(null);
    const [hasLongText, setHasLongText] = useState(false);
    useLayoutEffect(() => {
        const element = textRef.current;
        if (!element) return;
        const measure = () => {
            const style = window.getComputedStyle(element);
            const lineHeight = parseFloat(style.lineHeight) || (parseFloat(style.fontSize) || 16) * 1.7;
            setHasLongText(element.scrollHeight > lineHeight * 5 + 1);
        };
        measure();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        observer?.observe(element);
        window.addEventListener('resize', measure);
        return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
    }, [review.text, expanded]);
    return <article className="review-card">
        <h2>{review.course_name}</h2><Stars value={review.stars} />
        <blockquote id={`review-text-${review.id}`} ref={textRef} className={expanded ? undefined : 'review-text-preview'}>{review.text}</blockquote>
        {hasLongText && <Button label={expanded ? 'Show less' : 'Read more'} text size="small" className="review-expand"
            aria-label={`${expanded ? 'Show less' : 'Read more'}: ${review.course_name}`} aria-expanded={expanded}
            aria-controls={`review-text-${review.id}`} onClick={onToggle} />}
        <footer><span>Review by: [redacted]</span><time dateTime={review.created_at}>{new Date(review.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</time></footer>
    </article>;
}

export default function ReviewsPage() {
    const api = useApi();
    const auth = useAuth();
    const authenticated = auth.getIsAuthenticated();
    const authLoading = auth.getIsLoading();
    const identity = authenticated ? auth.getUser()?.sub ?? '' : '';
    const [courseKey, selectCourse] = useBookmarkSelection('course');
    const [pageKey, selectPage] = useBookmarkSelection('page');
    const [sortKey, selectSort] = useBookmarkSelection('sort');
    const sort = reviewSortOptions.some(option => option.value === sortKey) ? sortKey! : 'newest';
    const page = pageKey && /^[1-9][0-9]{0,5}$/.test(pageKey) ? Number(pageKey) : 1;
    const [reviews, setReviews] = useState<PublicReviewsDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [courses, setCourses] = useState<CourseFeedbackPromptDto[]>([]);
    const [coursesIdentity, setCoursesIdentity] = useState<string | null>(null);
    const eligibleCourses = coursesIdentity === identity && authenticated && !authLoading ? courses : [];
    const [coursesLoading, setCoursesLoading] = useState(false);
    const [coursesError, setCoursesError] = useState('');
    const [revision, setRevision] = useState(0);
    const [courseRevision, setCourseRevision] = useState(0);
    const [writing, setWriting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [expandedReviewId, setExpandedReviewId] = useState<number | null>(null);

    useEffect(() => {
        if (!api) return;
        const controller = new AbortController();
        setLoading(true); setError(''); setReviews(null); setExpandedReviewId(null);
        api.getPublicReviews(page, courseKey, controller.signal, sort).then(result => {
            if (!controller.signal.aborted) setReviews(result);
        }).catch(() => {
            if (!controller.signal.aborted) setError('Could not load reviews. Please try again.');
        }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [api, page, courseKey, sort, revision]);

    useEffect(() => {
        const controller = new AbortController();
        setCourses([]); setCoursesIdentity(null); setCoursesError(''); setCoursesLoading(false); setWriting(false);
        if (!api || authLoading || !authenticated) return;
        setCoursesLoading(true);
        api.getFeedbackCourses(controller.signal).then(result => {
            if (!controller.signal.aborted) { setCourses(result); setCoursesIdentity(identity); }
        }).catch(() => {
            if (!controller.signal.aborted) setCoursesError('Could not load your completed products. Please try again.');
        }).finally(() => { if (!controller.signal.aborted) setCoursesLoading(false); });
        return () => controller.abort();
    }, [api, authenticated, authLoading, identity, courseRevision]);

    useEffect(() => { setSubmitted(false); }, [identity, authenticated]);

    return <div className="reviews-page theme-content">
        <div className="reviews-heading">
            <div><h1 className="public-page-title">Product reviews</h1><p>Read feedback from teachers who have completed our products.</p></div>
        </div>
        {submitted && <p className="reviews-feedback-status" role="status">Thank you for your feedback. If you chose to share it, it will appear here after admin approval.</p>}
        {authenticated && coursesError && <div role="alert" className="reviews-feedback-status">{coursesError} <Button label="Retry completed products" text onClick={() => setCourseRevision(value => value + 1)} /></div>}
        {loading ? <div className="p-5 text-center" role="status"><ProgressSpinner aria-label="Loading reviews" /></div>
            : error ? <div role="alert" className="reviews-empty">{error} <Button label="Retry reviews" onClick={() => setRevision(value => value + 1)} /></div>
            : reviews && <>
                <div className="reviews-tools">
                    <div className="reviews-average">
                        {reviews.average_stars !== null && <><strong>{reviews.average_stars.toFixed(1)}</strong><Stars value={reviews.average_stars} /></>}
                        <span>{reviews.total} published {reviews.total === 1 ? 'review' : 'reviews'}</span>
                    </div>
                    <div className="reviews-selectors">
                    <div className="reviews-filter"><label htmlFor="reviews-product">Filter by product</label>
                        <Dropdown inputId="reviews-product" value={courseKey} options={reviews.courses} optionLabel="course_name" optionValue="course_key"
                            placeholder="All products" showClear filter disabled={reviews.courses.length === 0} className="w-full" onChange={event => { selectCourse(event.value ?? null); selectPage(null, true); }} />
                    </div>
                    <div className="reviews-filter"><label htmlFor="reviews-sort">Sort by</label>
                        <Dropdown inputId="reviews-sort" value={sort} options={reviewSortOptions} optionLabel="label" optionValue="value"
                            className="w-full" onChange={event => { selectSort(event.value === 'newest' ? null : event.value); selectPage(null, true); }} />
                    </div>
                    </div>
                </div>
                {reviews.data.length === 0 ? <p className="reviews-empty">{courseKey ? 'No published reviews for this product yet.' : 'No published reviews yet. Reviews will appear here after learners choose to share them.'}</p>
                    : <div className="reviews-grid">{reviews.data.map(review => <ReviewCard key={review.id} review={review}
                        expanded={expandedReviewId === review.id} onToggle={() => setExpandedReviewId(current => current === review.id ? null : review.id)} />)}</div>}
                {reviews.total > 0 && <Paginator className="reviews-pagination" first={(reviews.page - 1) * reviews.page_size} rows={reviews.page_size} totalRecords={reviews.total}
                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                    currentPageReportTemplate="Showing {first}–{last} of {totalRecords} reviews"
                    onPageChange={event => selectPage(event.page === 0 ? null : String(event.page + 1))} />}
            </>}
        {eligibleCourses.length > 0 && <div className="reviews-actions">
            <Button label="Leave review" text size="small" disabled={coursesLoading} onClick={() => setWriting(true)} />
        </div>}
        {writing && eligibleCourses.length > 0 && <CourseFeedbackDialog key={identity} courses={eligibleCourses} onDone={() => setWriting(false)} onSubmitted={() => {
            setSubmitted(true); setCourseRevision(value => value + 1);
        }} />}
    </div>;
}
